import { type JSX, type FC, useState } from "react"
import styled from "styled-components"

import { type PurchaseableType } from "@forento/shared/models/product"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import { useUser } from "~/contexts/UserContext"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

import Button from "./Button"
import Hero from "./Hero"
import PurchaseItem from "./PurchaseItem"

type Product = {
	id: number
	name: string
	thumbnailUrl: string | null
	shortDescription: string | null
	longDescriptionHtml: string | null
	presentationHtml: string | null
	tags?: { id: number; label: string }[]
} & (
	| { status: "sign-in-required" | "membership-required" | "bokadero-membership-required" | "paused" | "unlocked" }
	| { status: "paywall"; price: { amount: number; currency: string } }
)

type Props = {
	type: PurchaseableType
	product: Product
	unlockedContent: JSX.Element | null
	reload(): Promise<unknown>
}
const ProductDetail: FC<Props> = ({ type, product, unlockedContent, reload }) => {
	const user = useUser()
	const t = useTranslation()

	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	return (
		<PurchaseSuccessModalContextProvider content={{ type, id: product.id }} paypalOrder={paypalOrder}>
			<Hero
				title={product.name}
				thumbnailUrl={product.thumbnailUrl}
				tags={product.tags}
				htmlDescription={product.longDescriptionHtml ?? undefined}
				callToAction={
					product.status === "paywall" ? (
						<PurchaseItem
							item={{
								type,
								id: product.id,
								name: product.name,
								shortDescription: product.shortDescription,
								price: product.price,
								accessTimeMonths: null,
							}}
							onPurchase={async ({ paypalOrder }) => {
								setPaypalOrder(paypalOrder)
								await reload()
							}}
						/>
					) : product.status === "sign-in-required" ? (
						<CallToAction>
							<p>{t("product.onlyForUsers")}</p>
							<ButtonsRow>
								<Button variant="primary" onClick={routes.user.signin()}>
									{t("user.signIn.title")}
								</Button>
								<Button variant="primary" onClick={routes.user.signup()}>
									{t("user.signUp.title")}
								</Button>
							</ButtonsRow>
						</CallToAction>
					) : product.status === "membership-required" ? (
						<CallToAction>
							<p>{t("product.onlyForMembers")}</p>
							<ButtonsRow>
								<Button variant="primary" onClick={routes.membership.index()}>
									{t("membership.title")}
								</Button>
							</ButtonsRow>
						</CallToAction>
					) : product.status === "bokadero-membership-required" ? (
						<CallToAction>
							<p>Gratis för medlemmar i Bokadero Arena</p>
							<ButtonsRow>
								{user.user ? (
									<Button variant="primary" onClick={routes.user.settings()}>
										Hantera inställningar
									</Button>
								) : (
									<Button variant="primary" onClick={routes.user.signup()}>
										Skapa konto
									</Button>
								)}
							</ButtonsRow>
						</CallToAction>
					) : product.status === "paused" ? (
						<CallToAction>
							<p>{t("product.paused")}</p>
						</CallToAction>
					) : product.status === "unlocked" ? (
						unlockedContent
					) : (
						exhaustiveGuard(product.status)
					)
				}
			/>
			{product.presentationHtml !== null && (
				<MoreContent>
					<div className="editorjs" dangerouslySetInnerHTML={{ __html: product.presentationHtml }} />
				</MoreContent>
			)}
		</PurchaseSuccessModalContextProvider>
	)
}

const CallToAction = styled.div`
	max-width: 400px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	text-align: center;
`

const ButtonsRow = styled.div`
	display: flex;
	gap: 16px;

	@media (max-width: 499px) {
		flex-direction: column;
	}
`

export const MoreContent = styled.div`
	background-color: white;
	box-shadow: 0px 4px 30px 0px rgba(223, 232, 255, 0.25);
	padding: 24px;

	@media (min-width: 1000px) {
		padding: 48px 86px;
	}
`

export default ProductDetail
